import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { resetPassword } from '../api/auth';
import { PasswordField } from '../components/auth/PasswordField';

export function ResetPasswordScreen() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    if (password.length < 6) return setError('Password must be at least 6 characters.');
    if (password !== confirm) return setError('Passwords do not match.');
    setSubmitting(true);
    try {
      await resetPassword(token, password);
      setDone(true);
      setTimeout(() => navigate('/login', { replace: true }), 1400);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not reset password.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="adaptive-page auth-screen">
      <div className="auth-card">
        <span className="section-kicker">Reset password</span>
        <h1>Choose a new password</h1>
        <p>Enter a new password for your account.</p>

        {!token && <div className="auth-error">This reset link is missing its token.</div>}
        {done ? (
          <div className="acc-success">Password reset. Redirecting you to log in…</div>
        ) : (
          <form className="auth-form" onSubmit={onSubmit}>
            {error && (
              <div className="auth-error" role="alert">
                {error}
              </div>
            )}
            <PasswordField
              label="New password"
              placeholder="New password (min 6 chars)"
              autoComplete="new-password"
              minLength={6}
              value={password}
              onChange={setPassword}
            />
            <PasswordField
              label="Confirm password"
              placeholder="Confirm new password"
              autoComplete="new-password"
              value={confirm}
              onChange={setConfirm}
            />
            <button type="submit" className="primary-action" disabled={submitting || !token}>
              {submitting ? 'Resetting…' : 'Reset password'}
              {!submitting && <ArrowRight size={18} aria-hidden="true" />}
            </button>
          </form>
        )}

        <div className="auth-links">
          <Link to="/login">Back to log in</Link>
        </div>
      </div>
    </section>
  );
}
